@if (loading) {
  <div class="loading-container">
    <div class="loading-spinner"></div>
    <p>{{ 'manageEvent.tasks.loading' | translate }}</p>
  </div>
} @else {
  <!-- Header -->
  <div class="d-flex flex-wrap flex-stack mb-6">
    <div class="my-2">
      <h3 class="fw-bold mb-1">{{ 'manageEvent.tasks.title' | translate }}</h3>
      @if (tasks.length > 0) {
        <span class="text-muted fs-7">
          {{ completedTasks.length }}/{{ filteredTasks.length }} {{ 'manageEvent.tasks.completed' | translate | lowercase }}
        </span>
      }
    </div>
    <div class="d-flex flex-wrap my-2 gap-3 align-items-center">
      @if (tasks.length > 0 && (contacts.length > 0 || members.length > 0)) {
        <select class="form-select" style="width: 200px;" [(ngModel)]="filterAssignee">
          <option [ngValue]="null">{{ 'manageEvent.tasks.allAssignees' | translate }}</option>
          @if (members.length > 0) {
            <optgroup [label]="'manageEvent.members.membersSection' | translate">
              @for (member of members; track member.member_id) {
                <option [ngValue]="'member:' + member.user_id">{{ member.user?.first_name }} {{ member.user?.last_name }}</option>
              }
            </optgroup>
          }
          @if (contacts.length > 0) {
            <optgroup [label]="'manageEvent.tasks.contacts' | translate">
              @for (contact of contacts; track contact.contact_id) {
                <option [ngValue]="'contact:' + contact.contact_id">{{ contact.first_name }} {{ contact.last_name }}</option>
              }
            </optgroup>
          }
        </select>
      }
      <app-button
        [variant]="'primary'"
        [label]="'manageEvent.tasks.addTask' | translate"
        [iconClass]="'fa fa-plus'"
        (clicked)="openAddForm()"
      />
    </div>
  </div>

  @if (tasks.length === 0) {
    <!-- Empty State -->
    <div class="card">
      <div class="card-body text-center py-10">
        <i class="fa fa-clipboard-list fs-3x text-muted mb-5"></i>
        <h4 class="text-muted">{{ 'manageEvent.tasks.noTasks' | translate }}</h4>
        <p class="text-muted">{{ 'manageEvent.tasks.noTasksHint' | translate }}</p>
        <app-button
          [variant]="'primary'"
          [label]="'manageEvent.tasks.addFirstTask' | translate"
          [iconClass]="'fa fa-plus'"
          (clicked)="openAddForm()"
        />
      </div>
    </div>
  } @else {
    <!-- Progress Bar -->
    @if (filteredTasks.length > 0) {
      <div class="progress-bar-wrapper mb-5">
        <div class="progress-track">
          <div class="progress-fill" [style.width.%]="(completedTasks.length / filteredTasks.length) * 100"></div>
        </div>
      </div>
    }

    <!-- Quick Add -->
    <div class="quick-add mb-5">
      <div class="quick-add-inner">
        <i class="fa fa-plus quick-add-icon"></i>
        <input
          type="text"
          class="quick-add-input"
          [(ngModel)]="quickAddTitle"
          [placeholder]="'manageEvent.tasks.quickAddPlaceholder' | translate"
          (keyup.enter)="quickAdd()"
        />
        @if (quickAddTitle.trim()) {
          <app-button
            [variant]="'primary'"
            [size]="'sm'"
            [label]="'manageEvent.tasks.add' | translate"
            [loading]="quickAddLoading"
            (clicked)="quickAdd()"
          />
        }
      </div>
    </div>

    <!-- Remaining Tasks -->
    @if (remainingTasks.length > 0) {
      <div class="card mb-5">
        <div class="card-body p-0">
          <div class="task-list">
            @for (task of remainingTasks; track task.task_id) {
              <div class="task-item">
                <button class="task-check-btn" (click)="toggleTask(task)" [class.toggling]="togglingTaskId === task.task_id">
                  @if (togglingTaskId === task.task_id) {
                    <i class="fa fa-spinner fa-spin"></i>
                  } @else {
                    <i class="fa-regular fa-circle"></i>
                  }
                </button>
                <div class="task-body" (click)="openEditForm(task)">
                  <div class="task-title">{{ task.title }}</div>
                  @if (task.description) {
                    <div class="task-desc">{{ task.description }}</div>
                  }
                  <div class="task-badges">
                    @if (getTaskAssigneeName(task)) {
                      <span class="task-badge task-badge-assignee">
                        <i class="fa fa-user me-1"></i>{{ getTaskAssigneeName(task) }}
                      </span>
                    }
                    @if (task.due_date) {
                      <span class="task-badge" [ngClass]="getDueDateBadgeClass(task)">
                        <i class="fa fa-calendar me-1"></i>{{ getDueDateLabel(task) }}
                      </span>
                    }
                  </div>
                </div>
                <div class="task-actions">
                  <button class="btn-action" (click)="openEditForm(task)" title="{{ 'common.edit' | translate }}">
                    <i class="fa fa-pen"></i>
                  </button>
                  <button class="btn-action btn-action-danger" (click)="deleteTask(task)" title="{{ 'common.delete' | translate }}">
                    <i class="fa fa-trash"></i>
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Completed Tasks -->
    @if (completedTasks.length > 0) {
      <div class="completed-header" (click)="showCompleted = !showCompleted">
        <i class="fa fa-chevron-right completed-chevron" [class.open]="showCompleted"></i>
        <span>{{ 'manageEvent.tasks.completedSection' | translate }} ({{ completedTasks.length }})</span>
      </div>
      @if (showCompleted) {
        <div class="card">
          <div class="card-body p-0">
            <div class="task-list">
              @for (task of completedTasks; track task.task_id) {
                <div class="task-item task-item-completed">
                  <button class="task-check-btn task-check-btn-done" (click)="toggleTask(task)" [class.toggling]="togglingTaskId === task.task_id">
                    @if (togglingTaskId === task.task_id) {
                      <i class="fa fa-spinner fa-spin"></i>
                    } @else {
                      <i class="fa fa-circle-check"></i>
                    }
                  </button>
                  <div class="task-body">
                    <div class="task-title task-title-done">{{ task.title }}</div>
                    <div class="task-badges">
                      @if (task.completed_by) {
                        <span class="task-badge task-badge-completed">
                          <i class="fa fa-check me-1"></i>
                          {{ task.completed_by.first_name }}
                          @if (task.completed_at) {
                            &middot; {{ task.completed_at | date:'dd. MMM' }}
                          }
                        </span>
                      }
                      @if (getTaskAssigneeShortName(task)) {
                        <span class="task-badge task-badge-assignee">
                          <i class="fa fa-user me-1"></i>{{ getTaskAssigneeShortName(task) }}
                        </span>
                      }
                    </div>
                  </div>
                  <div class="task-actions">
                    <button class="btn-action btn-action-danger" (click)="deleteTask(task)" title="{{ 'common.delete' | translate }}">
                      <i class="fa fa-trash"></i>
                    </button>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      }
    }
  }
}

<!-- Add/Edit Task Modal -->
@if (showForm) {
  <div class="modal-backdrop fade show"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content border-0 shadow-lg" style="border-radius: 16px; overflow: hidden;">
        <div class="modal-header border-0 pb-0">
          <h5 class="modal-title fw-bold">
            @if (editingTask) {
              {{ 'manageEvent.tasks.editTask' | translate }}
            } @else {
              {{ 'manageEvent.tasks.addTask' | translate }}
            }
          </h5>
          <button type="button" class="btn-close" (click)="cancelForm()"></button>
        </div>
        <div class="modal-body pt-4">
          <div class="mb-4">
            <app-input-component
              [label]="'manageEvent.tasks.taskTitle' | translate"
              [(value)]="formTitle"
              (keyup.enter)="saveTask()"
            />
          </div>
          <div class="mb-4">
            <label class="form-label text-muted small fw-semibold">{{ 'manageEvent.tasks.taskDescription' | translate }}</label>
            <textarea class="form-control" rows="2" [(ngModel)]="formDescription"
                      [placeholder]="'manageEvent.tasks.taskDescriptionPlaceholder' | translate"
                      style="border-radius: 10px;"></textarea>
          </div>
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label text-muted small fw-semibold">{{ 'manageEvent.tasks.assignee' | translate }}</label>
              <select class="form-select" [(ngModel)]="formAssignee" style="border-radius: 10px;">
                <option [ngValue]="null">{{ 'manageEvent.tasks.unassigned' | translate }}</option>
                @if (members.length > 0) {
                  <optgroup [label]="'manageEvent.members.membersSection' | translate">
                    @for (member of members; track member.member_id) {
                      <option [ngValue]="'member:' + member.user_id">{{ member.user?.first_name }} {{ member.user?.last_name }}</option>
                    }
                  </optgroup>
                }
                @if (contacts.length > 0) {
                  <optgroup [label]="'manageEvent.tasks.contacts' | translate">
                    @for (contact of contacts; track contact.contact_id) {
                      <option [ngValue]="'contact:' + contact.contact_id">{{ contact.first_name }} {{ contact.last_name }}</option>
                    }
                  </optgroup>
                }
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label text-muted small fw-semibold">{{ 'manageEvent.tasks.dueDate' | translate }}</label>
              <input type="date" class="form-control" [(ngModel)]="formDueDate" style="border-radius: 10px;">
            </div>
          </div>
        </div>
        <div class="modal-footer border-0 pt-0">
          <app-button [variant]="'neutral'" [label]="'common.cancel' | translate" (clicked)="cancelForm()" />
          <app-button [variant]="'primary'" [label]="'common.save' | translate" (clicked)="saveTask()" [disabled]="!formTitle.trim() || formLoading" [loading]="formLoading" />
        </div>
      </div>
    </div>
  </div>
}
